import Vue from "vue";
import http from "@/utils/service/http.js";
import plans from "@storage/app/plans.json";

export const BILLING_PERIOD_MONTHLY = "monthly";
export const BILLING_PERIOD_YEARLY = "yearly";
export const BILLING_PERIOD_QUARTERLY = "quarterly";
export const BILLING_PERIOD_BIENNIAL = "biennial";
export const BILLING_PERIOD_TRIENNIAL = "triennial";
export const BILLING_PERIOD_ONE_TIME_0 = "one-time-0";
export const BILLING_PERIOD_ONE_TIME_6 = "one-time-6";
export const BILLING_PERIOD_ONE_TIME_12 = "one-time-12";

export const SUBSCRIPTION = "subscription";
export const ONE_TIME = "one-time";

export const allKnownPeriods = [
    BILLING_PERIOD_MONTHLY,
    BILLING_PERIOD_YEARLY,
    BILLING_PERIOD_QUARTERLY,
    BILLING_PERIOD_BIENNIAL,
    BILLING_PERIOD_TRIENNIAL,
    BILLING_PERIOD_ONE_TIME_0,
    BILLING_PERIOD_ONE_TIME_6,
    BILLING_PERIOD_ONE_TIME_12,
];

const pricingService = {
    BILLING_PERIOD_MONTHLY,
    BILLING_PERIOD_YEARLY,
    BILLING_PERIOD_QUARTERLY,
    BILLING_PERIOD_BIENNIAL,
    BILLING_PERIOD_TRIENNIAL,
    plansPricing: plans,

    async plansIndex(locale = null) {
        this.plansPricing = await http.get("/api/v1/plans", {
            params: { locale },
        });
        return this.planPricing;
    },

    periodToBillingPeriod(period) {
        switch (period) {
            case BILLING_PERIOD_ONE_TIME_0:
                return BILLING_PERIOD_ONE_TIME_0;
            case BILLING_PERIOD_ONE_TIME_6:
                return BILLING_PERIOD_ONE_TIME_6;
            case BILLING_PERIOD_ONE_TIME_12:
                return BILLING_PERIOD_ONE_TIME_12;
            case 3:
            case "3":
            case BILLING_PERIOD_QUARTERLY:
                return BILLING_PERIOD_QUARTERLY;
            case 12:
            case "12":
            case BILLING_PERIOD_YEARLY:
                return BILLING_PERIOD_YEARLY;
            case 24:
            case "24":
            case BILLING_PERIOD_BIENNIAL:
                return BILLING_PERIOD_BIENNIAL;
            case 36:
            case "36":
            case BILLING_PERIOD_TRIENNIAL:
                return BILLING_PERIOD_TRIENNIAL;
            case 1:
            case "1":
            case BILLING_PERIOD_MONTHLY:
            default:
                return BILLING_PERIOD_MONTHLY;
        }
    },

    billingPeriodToMonths(billingPeriod) {
        switch (billingPeriod) {
            case BILLING_PERIOD_TRIENNIAL:
                return 36;
            case BILLING_PERIOD_BIENNIAL:
                return 24;
            case BILLING_PERIOD_ONE_TIME_12:
            case BILLING_PERIOD_YEARLY:
                return 12;
            case BILLING_PERIOD_ONE_TIME_6:
                return 6;
            case BILLING_PERIOD_ONE_TIME_0:
                return 1;
            case BILLING_PERIOD_QUARTERLY:
                return 3;
            case BILLING_PERIOD_MONTHLY:
            default:
                return 1;
        }
    },

    isPeriodOneTimePeriod(billingPeriod) {
        switch (billingPeriod) {
            case BILLING_PERIOD_ONE_TIME_0:
            case BILLING_PERIOD_ONE_TIME_6:
            case BILLING_PERIOD_ONE_TIME_12:
                return true;
            case BILLING_PERIOD_YEARLY:
            case BILLING_PERIOD_MONTHLY:
            case BILLING_PERIOD_QUARTERLY:
            case BILLING_PERIOD_BIENNIAL:
            case BILLING_PERIOD_TRIENNIAL:
            default:
                return false;
        }
    },

    billingPeriodToPerPeriod(billingPeriod, short = false) {
        switch (billingPeriod) {
            case BILLING_PERIOD_YEARLY:
                return short ? "yr" : "year";
            case BILLING_PERIOD_QUARTERLY:
                return short ? "3mo" : "3 months";
            case BILLING_PERIOD_BIENNIAL:
                return short ? "2yr" : "2 years";
            case BILLING_PERIOD_TRIENNIAL:
                return short ? "3yr" : "3 years";
            case BILLING_PERIOD_MONTHLY:
            default:
                return short ? "mo" : "month";
        }
    },

    priceForPeriodInMonths(price, billingPeriod) {
        return (
            parseFloat(price) / this.billingPeriodToMonths(billingPeriod)
        ).toFixed(2);
    },

    planPricing(plan, period = null) {
        const pricing = plan.prices;

        if (period) {
            return pricing.find(
                ({ billing_period }) =>
                    billing_period === this.periodToBillingPeriod(period)
            );
        }

        return pricing;
    },

    currentPrice(plan, yearlyPlansSelected = false) {
        const pricingForPlan = this.planPricing(plan);

        if (yearlyPlansSelected && this.yearlyPlan(plan, pricingForPlan)) {
            return this.yearlyPlan(plan, pricingForPlan);
        }

        return this.monthlyPrice(plan, pricingForPlan);
    },

    monthlyPrice(plan, pricing = null) {
        if (!pricing) pricing = this.planPricing(plan);
        const monthlyPricing = pricing.find(
            ({ billing_period }) => billing_period === BILLING_PERIOD_MONTHLY
        );
        return {
            price: monthlyPricing?.price ?? 1.99,
            old_price: monthlyPricing?.old_price ?? 0,
            currency: monthlyPricing.currency,
        };
    },

    yearlyPlan(plan, pricing = null) {
        if (!pricing) pricing = this.planPricing(plan);
        let price = pricing.find(
            ({ billing_period }) => billing_period === BILLING_PERIOD_YEARLY
        );

        if (!price) return {};

        return {
            price: parseFloat(
                parseFloat(price.price) /
                    this.billingPeriodToMonths(BILLING_PERIOD_YEARLY)
            ).toFixed(2),
            old_price: parseFloat(
                parseFloat(price.old_price) /
                    this.billingPeriodToMonths(BILLING_PERIOD_YEARLY)
            ).toFixed(2),
            currency: price.currency,
        };
    },

    monthPeriodPrice(pricingPeriod, price = "price") {
        if (pricingPeriod[price] === 0) return 0;

        return parseFloat(
            parseFloat(pricingPeriod[price]) /
                this.billingPeriodToMonths(pricingPeriod.billing_period)
        ).toFixed(2);
    },

    cheapestPeriod(plan) {
        return plan?.prices?.reduce((prev, current) => {
            if (!prev) return current;

            const prevPeriodMonthlyPrice = parseFloat(
                this.monthPeriodPrice(prev)
            );
            const currentPeriodMonthlyPrice = parseFloat(
                this.monthPeriodPrice(current)
            );

            return prevPeriodMonthlyPrice > currentPeriodMonthlyPrice
                ? current
                : prev;
        }, null);
    },
};

Vue.prototype.$billing = pricingService;
